import { router } from "@inertiajs/react";
import { Button, Stack } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import {
  ApprenticeObservationsFilter,
  type ApprenticeObservationsFilterType,
} from "../../../components/apprentice/ApprenticeObservatonsFilter";
import { ApprenticeViewTabs, ApprenticeViewTabValues } from "../../../components/apprentice/ApprenticeViewTabs";
import { ObservationTable } from "../../../components/observation/ObservationTable";
import { showDeleteModal } from "../../../helpers/modal";
import { getFullName } from "../../../helpers/stringHelper";
import { useYearContext } from "../../../hooks/useYearContext";
import { ContentShell } from "../../../layout/ContentShell";
import type { Apprentice } from "../../../models/apprentice";
import type { Coach } from "../../../models/coach";
import type { Observation } from "../../../models/observation";
import type { Pagination } from "../../../models/pagination";
import {
  apprentices_path,
  edit_observation_path,
  new2_observations_path,
  observation_path,
  observations_apprentice_path,
} from "../../../routes";

type ApprenticeObservationsProps = {
  apprentice: Apprentice;
  observations: Observation[];
  filter: ApprenticeObservationsFilterType;
  filterable_coaches: Coach[];
  pagination: Pagination;
};

export default function ApprenticeObservations({
  apprentice,
  observations,
  filter,
  filterable_coaches,
  pagination,
}: ApprenticeObservationsProps) {
  const { sort_key, sort_direction, ...filterFormData } = filter;

  const sortStatus: DataTableSortStatus<Observation> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };
  const { pathWithYearContext } = useYearContext();
  const [isFetching, setIsFetching] = useState(false);

  const onDelete = (observation: Observation) =>
    showDeleteModal({
      title: "Observation",
      onConfirm: () => router.delete(pathWithYearContext(observation_path, observation.id)),
      article: "die",
    });

  const reloadObservations = (filters: ApprenticeObservationsFilterType, sortStatus: DataTableSortStatus<Observation>, page: number) => {
    setIsFetching(true);
    router.get(
      pathWithYearContext(observations_apprentice_path, apprentice.id),
      {
        filter: {
          ...filters,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      {
        onFinish: () => setIsFetching(false),
      },
    );
  };

  const onFilterChange = (filterValues: ApprenticeObservationsFilterType) => reloadObservations(filterValues, sortStatus, pagination.page);
  const onSortStatusChange = (sortStatus: DataTableSortStatus<Observation>) =>
    reloadObservations(filterFormData, sortStatus, pagination.page);
  const onPageChange = (page: number) => reloadObservations(filterFormData, sortStatus, page);

  const onEdit = (observation: Observation) => router.get(pathWithYearContext(edit_observation_path, observation.id));
  const onNew = () => router.get(pathWithYearContext(new2_observations_path, apprentice.id));

  return (
    <ContentShell
      pageTitle={`Beobachtungen - ${getFullName(apprentice)}`}
      title={`Beobachtungen: ${getFullName(apprentice)}`}
      onBack={() => router.get(pathWithYearContext(apprentices_path))}
      actions={
        <Button leftSection={<IconPlus />} variant="light" onClick={onNew}>
          Neu
        </Button>
      }
    >
      <ApprenticeViewTabs apprenticeId={apprentice.id} activeTab={ApprenticeViewTabValues.OBSERVATIONS} />
      <Stack>
        <ApprenticeObservationsFilter filterableCoaches={filterable_coaches} filter={filter} onFilterChange={onFilterChange} />
        <ObservationTable
          observations={observations}
          onDelete={onDelete}
          onEdit={onEdit}
          onSortStatusChange={onSortStatusChange}
          sortStatus={sortStatus}
          pagination={pagination}
          onPageChange={onPageChange}
          fetching={isFetching}
        />
      </Stack>
    </ContentShell>
  );
}
